import { create } from "zustand";
import {
  readPersistedButtonSize,
  readPersistedAutoTopUp,
  readPersistedReduceMotion,
  writePersistedButtonSize,
  writePersistedAutoTopUp,
  writePersistedReduceMotion,
  type ButtonSizePreference,
} from "../lib/preferences";
import {
  readPersistedMute,
  readPersistedVolume,
  writePersistedMute,
  writePersistedVolume,
} from "../lib/sound";
import { clampVolume } from "../lib/audio_player";

/**
 * Cross-component UI state that doesn't belong in either the
 * game-state store (`useGameStore` — wire-driven snapshot) or the
 * auth slice (`useAuthStore` — identity + session). The spectator
 * drawer's open / closed flag lives here so the HUD's count chip
 * can toggle the drawer rendered by the `SpectatorPanel` slot
 * without prop-drilling through `App.tsx` — a store action drives
 * the toggle in place of a callback prop.
 *
 * Kept Zustand-out of the game store to avoid coupling presentation
 * mode flips with wire-state updates: a spectator joining shouldn't
 * trigger any wire-state subscribers, just the drawer + the chip.
 */
export interface UIState {
  /** `true` when the mobile spectator drawer is currently surfaced.
   *  Driven by the HUD count chip tap + the drawer's close CTA /
   *  Escape key / backdrop tap. Defaults to `false` so the drawer
   *  starts collapsed at session begin. */
  spectatorDrawerOpen: boolean;
  /** `true` when the user has muted sound cues. Hydrated from
   *  localStorage (`arkhe.sound.muted`) on store init so the
   *  preference survives reloads. The HUD menu's "음소거" toggle
   *  flips this; every cue dispatch (`playSound`) reads the
   *  current value before firing. */
  soundMuted: boolean;
  /** Playback volume in the [0, 1] range. Hydrated from
   *  localStorage (`arkhe.sound.volume`) — the settings panel's
   *  slider writes via `setSoundVolume` which clamps + persists. */
  soundVolume: number;
  /** Explicit motion-reduction override. The OS-level
   *  `prefers-reduced-motion` media query already disables
   *  animations CSS-side; this flag layers on top so users on
   *  motion-on systems can still opt out in-app. CSS keys off
   *  `html[data-reduce-motion="true"]` (set by the App-level
   *  effect). */
  /** The reader is AT A TABLE — the game screen is mounted.
   *
   *  Published by the game screen and read by the app-level
   *  service-worker update gate, which defers a new build for as long
   *  as this is true.
   *
   *  Not "mid-hand". A reload at a table does not merely interrupt a
   *  hand: the session survives it but the TABLE does not, and the
   *  reader lands back in the lobby with a pending-seat row to click.
   *  Doing that to someone unasked can cost them the seat entirely —
   *  three missed turns bench a player. So the update waits until they
   *  are somewhere a reload costs nothing, which is the lobby. A build
   *  the client genuinely cannot speak to is a different problem with
   *  its own answer: the min-client-version gate blocks and offers the
   *  reload itself.
   *
   *  A UI-level fact, so it lives in the eager store — the update gate
   *  mounts at the root, above the lazy game chunk, and must not pull
   *  the reducer graph into the entry bundle to ask the question. */
  atTable: boolean;
  reduceMotion: boolean;
  /** Keep my seat filled to the table maximum after each hand, funded
   *  from the wallet. Off unless the player turns it on — it spends
   *  without asking again. */
  autoTopUp: boolean;
  /** Action-button size preference. `"auto"` (default) keeps the
   *  standard 56 px surface; `"small"` / `"large"` resize the
   *  action buttons. CSS keys off `html[data-action-size]` (set by
   *  the App-level effect) to remap `--action-btn-height` /
   *  `--action-btn-font`. Hydrated from localStorage
   *  (`arkhe.button_size`) on store init. */
  buttonSize: ButtonSizePreference;
  /** `true` when the profile modal is open. HUD menu's
   *  "내 프로필" entry flips this; the modal owns its own
   *  Escape / backdrop dismiss path. Lives here (not local to
   *  HUD) so the modal can render at the App root + survive a
   *  HUD menu close. */
  profileOpen: boolean;
  /** `true` when the leaderboard modal is open. HUD menu's
   *  "리더보드" entry flips this; mirrors `profileOpen` so the
   *  modal renders at the App root + survives a HUD menu close. */
  leaderboardOpen: boolean;
  /** `true` when the table chat panel is open. HUD menu's
   *  "채팅" entry flips this; mirrors the other modal flags so
   *  the panel renders at the App root + survives a HUD menu
   *  close. */
  chatPanelOpen: boolean;
  /** `true` when the last-hand lookback overlay is open. HUD
   *  menu's "이전 핸드 보기" entry flips this on
   *  (`HAND_HISTORY_LOOKBACK_AUDIT` Gap 3). The overlay reads
   *  `useGameStore.last_hand_snapshot` and renders the
   *  just-completed hand's board / hole cards / action log /
   *  winners. Defaults to false; opens dismiss via the same
   *  Escape / backdrop / close-button pattern the other
   *  modals use. */
  lastHandOverlayOpen: boolean;
  /** `true` when the standalone settings bottom sheet is
   *  open. The HUD menu's "⚙ 환경설정" entry flips this on
   *  per the menu redesign spec — settings moved out of the
   *  inline menu surface into a dedicated panel so the
   *  navigation hub stays at 7 entries. */
  settingsPanelOpen: boolean;
  /** `true` when the help bottom sheet is open. The HUD
   *  menu's "❓ 도움말" entry flips this on. The help sheet
   *  ships real, self-contained help — a concise rules summary
   *  + a short FAQ + an entry point to the tutorial overlay (no
   *  "coming soon" teaser). */
  helpPanelOpen: boolean;
  /** `true` when the own-seat context menu is open. The
   *  felt-level own-seat tap handler flips this on per the
   *  menu redesign §2.5 / §6.3 — sit-out + leave triggers
   *  live here so the felt is the canonical entry point for
   *  per-seat critical actions. */
  seatContextMenuOpen: boolean;
  /** Chips owed as a dead blind on a sit-back the server held
   *  back, or `null` when nothing is pending. Lives here rather
   *  than inside the menu because either surface can start the
   *  flip — the plate's rejoin button or the menu's own row — and
   *  the prompt that asks post-or-wait is drawn in one place. */
  sitBackOwedAmount: number | null;
  /** Monotonic counter bumped each time a leave-confirm is
   *  requested from OUTSIDE the HUD top-bar (currently the
   *  own-seat context menu's "나가기"). The HUD watches this seq
   *  and opens its single leave-confirm panel when it advances —
   *  the unified single-confirm path replacing the retired
   *  Game-level mid-hand leave modal. A counter (not a boolean)
   *  so a second request after a cancel still re-triggers the
   *  effect. Starts at 0; the HUD's mount captures the initial
   *  value so it doesn't auto-open on first paint. */
  leaveConfirmRequestSeq: number;
  /** Session-revoke notice latch (B) — the standing obligation to
   *  TELL the user their account session was revoked. Set when the
   *  logout cascade enters (`runLogoutSequence`), carrying the
   *  revoke reason. Modeled as an independent one-way latch (the
   *  `upgradeRequired` pattern) so it OUTLIVES the auth/connection
   *  teardown that produced it — the teardown must NOT clear it (the
   *  prior one-render flash root). Cleared ONLY by the modal CTA /
   *  countdown completion or the next successful `login()`
   *  (`clearPendingNotices`). Lives on the EAGER UI slice so the
   *  App-level gate mounts the modal WITHOUT importing the game
   *  store into the cold-login entry chunk. */
  pendingRevokeNotice: { readonly reason: string } | null;
  /** Surface-displaced notice latch (A) — the standing obligation to
   *  TELL the user a newer surface took over this table slot. Set
   *  when this surface is displaced (`markSurfaceDisplaced`).
   *  Independent from the
   *  revoke latch (a different domain fact: the session is FINE; only
   *  this surface was displaced). Drives the take-over modal. Cleared
   *  ONLY by the take-over CTA (reconnect) or the user dismissing it,
   *  and never by any teardown. */
  pendingSurfaceNotice: { readonly reason: string } | null;
  /** `true` once the server's min-client-version gate rejects this
   *  build (user-approved policy 1) — set either by an HTTP
   *  `426 Upgrade Required` response (`session/authed_request.ts`) or
   *  by the WS upgrade-required close code (`ws/client.ts` →
   *  `markUpgradeRequired`). DISTINCT from the notice latches so the
   *  App-level gate can render a blocking "update required — refresh"
   *  surface instead of the revoke / surface-displaced notices or the
   *  ordinary reconnect banner. Once `true` it stays latched (the
   *  only resolution is a reload onto a fresh build), so a flapping
   *  socket can't toggle it back off. */
  upgradeRequired: boolean;
}

export interface UIActions {
  /** Open / close the mobile spectator drawer outright. */
  setSpectatorDrawerOpen(open: boolean): void;
  /** Flip the drawer state — the HUD chip dispatches this so a
   *  second tap on the chip dismisses without an explicit close. */
  toggleSpectatorDrawer(): void;
  /** Flip the global sound-mute flag + persist to localStorage. */
  toggleSoundMute(): void;
  /** Set the mute flag outright (used when the consumer needs
   *  deterministic state vs. a toggle race). */
  setSoundMuted(muted: boolean): void;
  /** Set the playback volume. Clamps to [0, 1] + persists to
   *  localStorage so the preference survives reloads. */
  setSoundVolume(volume: number): void;
  /** Flip auto top-up + persist. */
  toggleAutoTopUp(): void;
  /** Flip the reduce-motion override + persist. */
  toggleReduceMotion(): void;
  /** Set the reduce-motion override outright. */
  setAtTable(value: boolean): void;
  setReduceMotion(value: boolean): void;
  /** Select an action-button size preference + persist. The
   *  App-level `applyDocumentPreferences` effect picks up the
   *  change and flips `html[data-action-size]` so the CSS-driven
   *  token remap resizes the action buttons live. */
  setButtonSize(buttonSize: ButtonSizePreference): void;
  /** Open / close the profile modal outright. */
  setProfileOpen(open: boolean): void;
  /** Open / close the leaderboard modal outright. */
  setLeaderboardOpen(open: boolean): void;
  /** Open / close the chat panel outright. */
  setChatPanelOpen(open: boolean): void;
  /** Open / close the last-hand lookback overlay outright. */
  setLastHandOverlayOpen(open: boolean): void;
  /** Open / close the standalone settings bottom sheet. */
  setSettingsPanelOpen(open: boolean): void;
  /** Open / close the help bottom sheet. */
  setHelpPanelOpen(open: boolean): void;
  /** Open / close the own-seat context menu. */
  setSeatContextMenuOpen(open: boolean): void;
  setSitBackOwedAmount(amount: number | null): void;
  /** Request the HUD open its single leave-confirm panel (from a
   *  non-top-bar entry point — e.g. the own-seat context menu).
   *  Bumps `leaveConfirmRequestSeq`; the HUD effect reacts. */
  requestLeaveConfirm(): void;
  /** Latch the session-revoke notice (B). Called by
   *  `runLogoutSequence` on entry with the revoke reason. One-way
   *  set: a re-entry with an existing latch is a no-op so a
   *  multi-source revoke race doesn't churn the modal. The teardown
   *  that follows must NOT clear this — only `clearPendingNotices` /
   *  the modal CTA does. */
  setPendingRevokeNotice(reason: string): void;
  /** Latch the surface-displaced notice (A). Called by
   *  `markSurfaceDisplaced` with the displace reason. One-way set
   *  (idempotent re-entry). */
  setPendingSurfaceNotice(reason: string): void;
  /** Clear BOTH notice latches. The SOLE non-CTA clear path: invoked
   *  on a successful `login()` so a fresh session starts with a clean
   *  notice surface. Teardown paths (logout / felt drain / reset)
   *  intentionally do NOT call this — the notice survives the
   *  teardown that produced it. */
  clearPendingNotices(): void;
  /** Latch the upgrade-required state (user-approved policy 1). Both
   *  trigger sources — the HTTP `426` handler in `authed_request.ts`
   *  and the WS upgrade-required close in `ws/client.ts` — call this.
   *  Idempotent + one-way: once `true` it never flips back (the only
   *  resolution is a reload onto a fresh build), so a retry that also
   *  426s or a reconnect that re-closes with the upgrade code is a
   *  cheap no-op. */
  markUpgradeRequired(): void;
}

export const useUIStore = create<UIState & UIActions>((set) => ({
  spectatorDrawerOpen: false,
  soundMuted: readPersistedMute(),
  soundVolume: readPersistedVolume(),
  atTable: false,
  reduceMotion: readPersistedReduceMotion(),
  autoTopUp: readPersistedAutoTopUp(),
  buttonSize: readPersistedButtonSize(),
  profileOpen: false,
  leaderboardOpen: false,
  chatPanelOpen: false,
  lastHandOverlayOpen: false,
  settingsPanelOpen: false,
  helpPanelOpen: false,
  seatContextMenuOpen: false,
  sitBackOwedAmount: null,
  leaveConfirmRequestSeq: 0,
  pendingRevokeNotice: null,
  pendingSurfaceNotice: null,
  upgradeRequired: false,
  setSpectatorDrawerOpen(open) {
    set((s) => ({ ...s, spectatorDrawerOpen: open }));
  },
  toggleSpectatorDrawer() {
    set((s) => ({ ...s, spectatorDrawerOpen: !s.spectatorDrawerOpen }));
  },
  toggleSoundMute() {
    set((s) => {
      const next = !s.soundMuted;
      writePersistedMute(next);
      return { ...s, soundMuted: next };
    });
  },
  setSoundMuted(muted) {
    writePersistedMute(muted);
    set((s) => ({ ...s, soundMuted: muted }));
  },
  setSoundVolume(volume) {
    const clamped = clampVolume(volume);
    writePersistedVolume(clamped);
    set((s) => ({ ...s, soundVolume: clamped }));
  },
  toggleAutoTopUp() {
    set((s) => {
      const next = !s.autoTopUp;
      writePersistedAutoTopUp(next);
      return { ...s, autoTopUp: next };
    });
  },
  toggleReduceMotion() {
    set((s) => {
      const next = !s.reduceMotion;
      writePersistedReduceMotion(next);
      return { ...s, reduceMotion: next };
    });
  },
  setAtTable(value) {
    set((s) => (s.atTable === value ? s : { ...s, atTable: value }));
  },
  setReduceMotion(value) {
    writePersistedReduceMotion(value);
    set((s) => ({ ...s, reduceMotion: value }));
  },
  setButtonSize(buttonSize) {
    writePersistedButtonSize(buttonSize);
    set((s) => ({ ...s, buttonSize }));
  },
  setProfileOpen(open) {
    set((s) => ({ ...s, profileOpen: open }));
  },
  setLeaderboardOpen(open) {
    set((s) => ({ ...s, leaderboardOpen: open }));
  },
  setChatPanelOpen(open) {
    set((s) => ({ ...s, chatPanelOpen: open }));
  },
  setLastHandOverlayOpen(open) {
    set((s) => ({ ...s, lastHandOverlayOpen: open }));
  },
  setSettingsPanelOpen(open) {
    set((s) => ({ ...s, settingsPanelOpen: open }));
  },
  setHelpPanelOpen(open) {
    set((s) => ({ ...s, helpPanelOpen: open }));
  },
  setSeatContextMenuOpen(open) {
    set((s) => ({ ...s, seatContextMenuOpen: open }));
  },
  setSitBackOwedAmount(amount) {
    set((s) => ({ ...s, sitBackOwedAmount: amount }));
  },
  requestLeaveConfirm() {
    set((s) => ({
      ...s,
      leaveConfirmRequestSeq: s.leaveConfirmRequestSeq + 1,
    }));
  },
  setPendingRevokeNotice(reason) {
    set((s) =>
      s.pendingRevokeNotice !== null
        ? s
        : { ...s, pendingRevokeNotice: { reason } },
    );
  },
  setPendingSurfaceNotice(reason) {
    set((s) =>
      s.pendingSurfaceNotice !== null
        ? s
        : { ...s, pendingSurfaceNotice: { reason } },
    );
  },
  clearPendingNotices() {
    set((s) =>
      s.pendingRevokeNotice === null && s.pendingSurfaceNotice === null
        ? s
        : { ...s, pendingRevokeNotice: null, pendingSurfaceNotice: null },
    );
  },
  markUpgradeRequired() {
    set((s) => (s.upgradeRequired ? s : { ...s, upgradeRequired: true }));
  },
}));
